import { Archive, ArchiveRestore, CalendarPlus, KeyRound, Lock, Pencil, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/services";
import { BILLING_LABELS } from "@/lib/billing";

const STATUS = {
  active: ["Ativa", "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"],
  blocked: ["Bloqueada", "border-rose-500/40 bg-rose-500/15 text-rose-300"],
  archived: ["Arquivada", "border-slate-500/40 bg-slate-500/15 text-slate-300"],
};

const Act = ({ onClick, icon: Icon, label, testid, cls = "border-slate-700" }) => (
  <Button size="sm" variant="outline" onClick={onClick} className={`rounded-full bg-transparent ${cls}`} data-testid={testid}>
    <Icon className="mr-1 h-3.5 w-3.5" /> {label}
  </Button>
);

const daysText = (n) => (n == null ? "" : n > 0 ? ` · faltam ${n} dia(s)` : n === 0 ? " · vence hoje" : ` · venceu há ${-n} dia(s)`);

export const CompanyCard = ({ c, isOwn, onToggle, onReset, onEdit, onArchive, onRestore, onGrant }) => {
  const status = c.archived ? "archived" : c.active ? "active" : "blocked";
  const [label, cls] = STATUS[status];
  return (
    <div className={`rounded-xl border p-4 ${status === "active" ? "border-slate-700/60 bg-slate-800/60" : status === "blocked" ? "border-rose-500/30 bg-rose-500/5" : "border-slate-700/40 bg-slate-900/40"}`} data-testid={`company-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{c.company_name}</span>
            {isOwn && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-900">SUA</span>}
          </div>
          <div className="truncate text-xs text-slate-400">{c.responsible_name || c.owner_name} · {c.owner_email}{c.phone ? ` · ${c.phone}` : ""}</div>
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`company-status-${c.id}`}>{label}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div><div className="text-slate-500">Usuários</div><div className="font-mono text-slate-200">{c.users}</div></div>
        <div><div className="text-slate-500">Lançamentos</div><div className="font-mono text-slate-200">{c.services}</div></div>
        <div><div className="text-slate-500">Desde</div><div className="font-mono text-slate-200">{formatDate(c.created_at?.slice(0, 10))}</div></div>
      </div>
      <div className="mt-2 text-xs text-slate-400" data-testid={`company-billing-${c.id}`}>
        Assinatura: {c.billing.exempt ? "isenta" : `${BILLING_LABELS[c.billing.status] || c.billing.status} · até ${formatDate(c.billing.paid_until)}${daysText(c.billing.days_left)}`}
        {c.billing.expired && !c.archived && <span className="ml-1 font-semibold text-rose-300">(vencida)</span>}
      </div>
      {c.delete_after && <div className="mt-1 text-xs font-semibold text-amber-300" data-testid={`company-delete-after-${c.id}`}>Pediu exclusão: dados apagados em {formatDate(c.delete_after.slice(0, 10))}</div>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Act onClick={() => onEdit(c)} icon={Pencil} label="Editar" testid={`company-edit-${c.id}`} />
        {!c.archived && !c.billing.exempt && <Act onClick={() => onGrant(c)} icon={CalendarPlus} label="Liberar dias" testid={`company-grant-${c.id}`} cls="border-emerald-500/50 text-emerald-300" />}
        {!isOwn && !c.archived && (
          <Act onClick={() => onToggle(c)} icon={c.active ? Lock : Unlock} label={c.active ? "Bloquear" : "Ativar"} testid={`company-toggle-${c.id}`}
            cls={c.active ? "border-rose-500/50 text-rose-300" : "border-emerald-500/50 text-emerald-300"} />
        )}
        {!isOwn && !c.archived && <Act onClick={() => onReset(c)} icon={KeyRound} label="Redefinir senha" testid={`company-reset-${c.id}`} />}
        {!isOwn && !c.archived && <Act onClick={() => onArchive(c)} icon={Archive} label="Arquivar" testid={`company-archive-${c.id}`} />}
        {c.archived && <Act onClick={() => onRestore(c)} icon={ArchiveRestore} label="Reativar" testid={`company-restore-${c.id}`} cls="border-emerald-500/50 text-emerald-300" />}
      </div>
    </div>
  );
};
